import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getRouteInfo } from './breadcrumbRoutes';

export interface BreadcrumbEntry {
  label: string;
  path: string;
}

interface BreadcrumbContextValue {
  trail: BreadcrumbEntry[];
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({ trail: [] });

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [trail, setTrail] = useState<BreadcrumbEntry[]>([]);
  const prevKey = useRef<string | null>(null);

  useEffect(() => {
    const { pathname, search } = location;
    const key = pathname + search;
    if (key === prevKey.current) return;
    prevKey.current = key;

    const info = getRouteInfo(pathname);
    if (!info) return;

    const fullPath = search ? pathname + search : pathname;
    const entry: BreadcrumbEntry = { label: info.label, path: fullPath };

    const isEffectivelyTopLevel =
      info.topLevel && !(info.topLevelWhenNoParams && search);

    if (isEffectivelyTopLevel) {
      setTrail([entry]);
    } else {
      setTrail(prev => {
        const existingIdx = prev.findIndex(e => {
          const ePath = e.path.split('?')[0];
          if (ePath === pathname) return true;
          // Only treat ePath as ancestor if the route config says so (topLevel=false)
          const eInfo = getRouteInfo(ePath);
          return !eInfo?.topLevel && pathname.startsWith(ePath + '/');
        });
        if (existingIdx !== -1) {
          return [...prev.slice(0, existingIdx), entry];
        }
        // No parent in trail — auto-inject logical parent if defined.
        // Use prefix match so /vendor-list/edit/42 satisfies parent /vendor-list/edit.
        const parentAlreadyPresent = info.parent && prev.some(e => {
          const ePath = e.path.split('?')[0];
          return ePath === info.parent || ePath.startsWith(info.parent! + '/');
        });
        if (info.parent && !parentAlreadyPresent) {
          const parentInfo = getRouteInfo(info.parent!);
          if (parentInfo) {
            const parentEntry: BreadcrumbEntry = { label: parentInfo.label, path: info.parent! };
            return [...prev, parentEntry, entry];
          }
        }
        return [...prev, entry];
      });
    }
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BreadcrumbContext.Provider value={{ trail }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbTrail(): BreadcrumbEntry[] {
  return useContext(BreadcrumbContext).trail;
}
